// ListView.jsx

import React, { useMemo, useCallback } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Utilities
import { getSingularLabel } from 'utilities/Languages';
import { buildDetachEdgeLabel } from 'utilities/ViewRows';

// Services
import { deleteEdge } from 'services/nodes/NodeEdgeService';

// Hooks
import useDefaultNodeOpen from 'views/hooks/useDefaultNodeOpen';
import useIncrementalPagination from 'views/hooks/useIncrementalPagination';
import useListNodelistHydration from 'views/list/hooks/useListNodelistHydration';

// Constants
import { VIEW_PAGINATION } from 'views/constants/viewPagination';

// CSS
import './ListView.css';

// Components
import ListViewItem from './item/ListViewItem';
import { buildListViewItemData, resolveListViewItemConfig } from './item/listViewItemData';
import IconButton from 'components/iconbutton/IconButton';
import { toast } from 'react-toastify';
import { writeNodeDragData } from 'views/dragDrop/nodeDragData';

/**
 * ListView
 *
 * Renders a list of nodes in a scrollable view.
 *
 * @returns {ReactNode} - ListView.
 */
const ListView = React.memo(({
    view,
    typeDef,
    nodes = [],
    parentNodeKey = null,
    parentNodeTypeLabel = '',
    language = 'en',
    addTypeKey,
    onQuickActionSelect,
    onItemClick = null,
    maxNodes = null,
    enforceExplicitProperties = false,
    readOnly = false,
    nodeDragEnabled = false,
}) => {

    // ----- HANDLERS ----- //

    const handleOpenNode = useDefaultNodeOpen();
    // ----- CONTEXT ----- //

    const { getType } = useNodeTypes();
    const usesPerNodeTypeConfig = Boolean(view?.recordSearch);

    // ----- MEMOS ----- //

    const listItemConfig = useMemo(() => (
        usesPerNodeTypeConfig
            ? null
            : resolveListViewItemConfig({
                enforceExplicitProperties,
                typeDef,
                view,
            })
    ), [enforceExplicitProperties, typeDef, usesPerNodeTypeConfig, view]);

    const recordSearchView = useMemo(() => (
        usesPerNodeTypeConfig
            ? {
                ...view,
                catchAll: true,
                type: 'LIST',
            }
            : view
    ), [usesPerNodeTypeConfig, view]);

    const baseDisplayedNodes = useMemo(() => {
        if (!Array.isArray(nodes)) return [];
        if (!Number.isFinite(maxNodes) || maxNodes <= 0) return nodes;
        return nodes.slice(0, maxNodes);
    }, [nodes, maxNodes]);

    // Reset only when browse context changes — not when live snapshots replace `nodes`.
    const paginationResetKey = `${String(view?.key || '')}|${maxNodes ?? ''}`;

    // ----- HOOKS ----- //

    const {
        displayedItems: displayedNodes,
        hasMore,
        loadMoreRef,
    } = useIncrementalPagination({
        items: baseDisplayedNodes,
        initialCount: VIEW_PAGINATION.LIST.INITIAL,
        step: VIEW_PAGINATION.LIST.STEP,
        resetKey: paginationResetKey,
    });

    const getItemConfig = useCallback((node) => {
        if (usesPerNodeTypeConfig) {
            return resolveListViewItemConfig({
                enforceExplicitProperties: false,
                typeDef: getType(node?.type),
                view: recordSearchView,
            });
        }
        return listItemConfig;
    }, [getType, listItemConfig, recordSearchView, usesPerNodeTypeConfig]);

    const { resolveNodelistTitle } = useListNodelistHydration({
        nodes: displayedNodes,
        getItemConfig,
    });

    // ----- HANDLERS ----- //

    /**
 * Handles node click.
 *
 * @param {*} event
 * @param {*} node
 */

    const handleNodeClick = useCallback((event, node) => {
        // readOnly suppresses mutations elsewhere; opening Peek remains allowed.
        event?.preventDefault?.();
        if (typeof onItemClick === 'function') {
            event?.stopPropagation?.();
            onItemClick(node);
            return;
        }
        handleOpenNode(node);
    }, [handleOpenNode, onItemClick]);

    /**
 * Handles node double click.
 *
 * @param {*} event
 * @param {*} node
 */
    const handleNodeDoubleClick = useCallback((event, node) => {
        // Full-page node navigation stays blocked in read-only / visitor mode.
        if (readOnly) return;
        event?.preventDefault?.();
        if (typeof onItemClick === 'function') {
            event?.stopPropagation?.();
            return;
        }
        handleOpenNode(node);
    }, [handleOpenNode, onItemClick, readOnly]);

    /**
 * Handles detach node.
 *
 * @param {*} node
 */
    const handleDetachNode = useCallback(async (node) => {
        const parentKey = String(parentNodeKey || '').trim();
        const childKey = String(node?.key || '').trim();
        if (!parentKey || !childKey) return;

        try {
            await deleteEdge(parentKey, childKey);
        } catch (error) {
            toast.error(`Could not remove connection: ${error?.message || 'Unknown error'}`);
        }
    }, [parentNodeKey]);

    // ----- MEMOS ----- //

    const getDetachActionLabel = useCallback((node) => {
        const nodeTypeKey = String(node?.type || typeDef?.key || '').trim();
        const activeTypeKey = String(typeDef?.key || '').trim();
        const useActiveTypeDef = nodeTypeKey
            && activeTypeKey
            && nodeTypeKey.toUpperCase() === activeTypeKey.toUpperCase();
        const childNodeTypeLabel = useActiveTypeDef
            ? getSingularLabel(typeDef, nodeTypeKey, language)
            : nodeTypeKey;

        return buildDetachEdgeLabel({
            childNodeTypeLabel,
            parentNodeTypeLabel,
        });
    }, [language, parentNodeTypeLabel, typeDef]);

    const renderTopRightAction = useCallback((node) => {
        if (readOnly) return null;
        const childKey = String(node?.key || '').trim();
        if (!childKey) return null;

        const parentKey = String(parentNodeKey || '').trim();
        if (!parentKey) return null;

        return (
            <IconButton
              iconKey="do_not_disturb_on"
              iconSize={20}
              title={getDetachActionLabel(node)}
              onClick={(event) => {
                    event?.preventDefault?.();
                    event?.stopPropagation?.();
                    handleDetachNode(node);
                }}
            />
        );
    }, [getDetachActionLabel, handleDetachNode, parentNodeKey, readOnly]);
    // ----- RENDER ----- //

    return (
        <div className="list-view-container">
            {displayedNodes.map((node) => (
                (() => {
                    const nodeTypeDef = usesPerNodeTypeConfig ? getType(node?.type) : typeDef;
                    const itemConfig = usesPerNodeTypeConfig
                        ? resolveListViewItemConfig({
                            enforceExplicitProperties: false,
                            typeDef: nodeTypeDef,
                            view: recordSearchView,
                        })
                        : listItemConfig;
                    const itemData = buildListViewItemData({
                        config: itemConfig,
                        language,
                        node,
                        resolveNodelistTitle,
                    });

                    return (
                <ListViewItem
                  key={node?.key}
                  node={node}
                  coverUrl={itemData.coverUrl}
                  subtitle={itemData.subtitle}
                  enumPills={itemData.enumPills}
                  fieldValues={itemData.fieldValues}
                  topRightAction={renderTopRightAction(node)}
                  draggable={nodeDragEnabled && Boolean(node?.key)}
                  onDragStart={(event) => {
                        writeNodeDragData(event, node);
                    }}
                  onClick={(event) => handleNodeClick(event, node)}
                  onDoubleClick={(event) => handleNodeDoubleClick(event, node)}
                />
                    );
                })()
            ))}

            {/* LOAD MORE */}
            {hasMore && (

                <div className="list-view-load-more" ref={loadMoreRef} />
            )}
        </div>
    );

});

// Dev Tools
ListView.displayName = 'ListView';

export default ListView;
